"use client";

import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf';

import React, { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from './lib/supabaseClient';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Download, 
  MessageSquare, 
  Send, 
  Calendar,
  Clock,
  Star,
  X,
  MousePointer2,
  Hand,
  Edit,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  ArrowRight
} from "lucide-react";
import { Button } from "./components/ui/Button";
import { Textarea } from "./components/ui/Textarea";
import { Card } from "./components/ui/Card";
import { Badge } from "./components/ui/Badge";
import { Separator } from "./components/ui/Separator";
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import logo from './assets/logos/LogoA/Untitled-1.png';
import longLogo from './assets/logos/LogoLong/Untitled-2@0.5x.png';
import { sendEmailNotification, EmailService } from './lib/sendEmailNotification';
import MobileReviewPage from './reviewpage.mobile';
import { Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';

const getStatusBadge = (status: string, deadline: string | Date | null) => {
  const now = new Date();
  let deadlineDate: Date | null = null;
  if (deadline) {
    deadlineDate = typeof deadline === 'string' ? new Date(deadline) : deadline;
  }
  if (status === 'open' && deadlineDate && deadlineDate < now) {
    return (
      <Badge variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-100">
        <Clock className="w-3 h-3 mr-1" />
        Overdue
      </Badge>
    );
  }
  switch (status) {
    case 'open':
      return (
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Clock className="w-3 h-3 mr-1" />
          Open
        </Badge>
      );
    case 'changes-needed':
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          <AlertCircle className="w-3 h-3 mr-1" />
          Changes Needed
        </Badge>
      );
    case 'approved':
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

// Set pdfjs workerSrc after all imports
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';
// Stub ScrollArea and Tooltip for now
const ScrollArea: React.FC<{children: React.ReactNode, className?: string}> = ({children, className}) => <div className={className}>{children}</div>;
const TooltipProvider: React.FC<{children: React.ReactNode}> = ({children}) => <>{children}</>;
// Ensure tooltip wrapper allows pointer events and white background
const Tooltip: React.FC<{children: React.ReactNode}> = ({children}) => <span className="relative">{children}</span>;
const TooltipTrigger: React.FC<{asChild?: boolean, children: React.ReactNode}> = ({children}) => <>{children}</>;
// Update TooltipContent to have a white background - removed unused variable

// Types
type ToolMode = 'pointer' | 'hand' | 'annotation' | 'zoom-in' | 'zoom-out';

interface PendingAnnotation {
  x: number;
  y: number;
  page: number;
}

interface Annotation {
  id: string;
  x: number;
  y: number;
  page: number;
  content: string;
  timestamp: Date;
  author: string;
  avatar?: string;
  review_link?: string;
}

interface Comment {
  id: string;
  content: string;
  timestamp: Date;
  author: string;
  avatar?: string;
}

interface DocumentInfo {
  title: string;
  uploadDate: Date | null;
  deadline: Date | null;
  downloadUrl: string;
  totalPages: number;
  status: string; // add status property
  type: 'pdf' | 'image' | string;
  url: string;
}

// SupabaseReviewRequest interface removed - was unused

interface SupabaseAnnotation {
  id: number;
  review_link: string;
  page: number;
  x: number;
  y: number;
  comment: string;
  created_at: string;
  author_email?: string;
}

interface SupabaseComment {
  id: number;
  review_link: string;
  author_name: string;
  author_email: string;
  created_at: string;
  comment: string;
  parent_id: number | null;
}

// Vertical Toolbar Component
const VerticalToolbar: React.FC<{
  currentTool: ToolMode;
  onToolChange: (tool: ToolMode) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}> = ({ currentTool, onToolChange, onZoomIn, onZoomOut }) => {
  const tools = [
    { id: 'pointer' as ToolMode, icon: MousePointer2 },
    { id: 'hand' as ToolMode, icon: Hand },
    { id: 'annotation' as ToolMode, icon: Edit }
  ];

  return (
    <TooltipProvider>
      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 bg-card border rounded-lg p-2 shadow-lg">
        {tools.map(tool => (
          <Tooltip key={tool.id}>
            <TooltipTrigger asChild>
              <Button
                variant={currentTool === tool.id ? "default" : "ghost"}
                size="sm"
                className="h-10 w-10 p-0"
                onClick={() => onToolChange(tool.id)}
              >
                <tool.icon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            {/* TooltipContent removed for icon-only */}
          </Tooltip>
        ))}
        <div className="w-full h-px bg-border my-1" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0"
              onClick={onZoomIn}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0"
              onClick={onZoomOut}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

// Document Viewer Component
const DocumentViewer: React.FC<{
  document: DocumentInfo;
  annotations: Annotation[];
  onAddAnnotation?: (annotation: Omit<Annotation, 'id' | 'timestamp' | 'author'>) => Promise<any>;
  annotationError: string | null;
  setAnnotationError: (err: string | null) => void;
  isMobile: boolean;
}> = ({ document, annotations, onAddAnnotation, annotationError, setAnnotationError, isMobile }) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // Add page navigation
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTool, setCurrentTool] = useState<ToolMode>('pointer');
  const [pendingAnnotation, setPendingAnnotation] = useState<PendingAnnotation | null>(null);
  const [annotationText, setAnnotationText] = useState('');
  const viewerRef = useRef<HTMLDivElement>(null);
  // Ref to the white page container so we can measure precise coordinates
  const docRef = useRef<HTMLDivElement>(null);
  const [popupPosition, setPopupPosition] = useState<{ left: number; top: number } | null>(null);
  const [numPages, setNumPages] = useState(document.totalPages);
  // In DocumentViewer, add refs for each page
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 && currentTool === 'hand') { // Left click and pan tool
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && currentTool === 'hand') {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  // Prevent default drag of inner elements when using hand tool
  const handleDragStart = (e: React.DragEvent) => {
    if (currentTool === 'hand') {
      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Pointer event aliases (for browsers/devices where mouse events don’t fire)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') {
      console.log('onPointerDown', e);
    }
    handleMouseDown(e as unknown as React.MouseEvent);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') {
      console.log('onPointerMove', e);
    }
    handleMouseMove(e as unknown as React.MouseEvent);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') {
      console.log('onPointerUp', e);
    }
    handleMouseUp();
  };

  /**
   * Handle a click on the document surface while the annotation tool is active.
   * We now receive the page index explicitly so that annotations are always saved to the
   * correct page even if the user scrolled manually (which previously left `currentPage`
   * stuck at 1 and caused subsequent dots to be rendered off-screen).
   */
  const handleDocumentClick = (e: React.MouseEvent, pageIndex: number = 1) => {
    if (!isDragging && currentTool === 'annotation' && e.detail === 1) {
      const container = e.currentTarget as HTMLDivElement; // the clicked page/image wrapper
      const rect = container.getBoundingClientRect();
      // Calculate coordinates as percentages (0-1) instead of absolute pixels
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      setPendingAnnotation({ x, y, page: pageIndex });
      setAnnotationText('');

      // Show the text popup right where the user clicked
      setPopupPosition({ left: e.clientX, top: e.clientY });
    } else if (!isDragging) {
      if (currentTool === 'zoom-in' && e.detail === 1) {
        handleZoomIn();
      } else if (currentTool === 'zoom-out' && e.detail === 1) {
        handleZoomOut();
      }
    }
  };

  const handleAnnotationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (annotationText.trim() && pendingAnnotation) {
      try {
        setAnnotationError(null);
        // Immediately clear pending annotation so the dot disappears
        setPendingAnnotation(null);
        setAnnotationText('');
        setPopupPosition(null);
        // Keep annotation tool selected for multiple annotations
        setCurrentTool('annotation');
        if (onAddAnnotation) {
          await onAddAnnotation({
            x: pendingAnnotation.x,
            y: pendingAnnotation.y,
            page: pendingAnnotation.page,
            content: annotationText.trim()
          });
        }
      } catch (err: any) {
        setAnnotationError('Failed to add annotation: ' + (err?.message || String(err)));
        console.error('Annotation insert error', err);
        setPendingAnnotation(null);
        setAnnotationText('');
        setPopupPosition(null);
        setCurrentTool('annotation');
      }
    } else {
      setPendingAnnotation(null);
      setAnnotationText('');
      setPopupPosition(null);
      setCurrentTool('annotation');
    }
  };

  const handleAnnotationCancel = () => {
    setPendingAnnotation(null);
    setAnnotationText('');
    setPopupPosition(null);
    setCurrentTool('annotation');
  };

  const getCursor = () => {
    switch (currentTool) {
      case 'hand': return isDragging ? 'grabbing' : 'grab';
      case 'annotation': return 'crosshair';
      case 'zoom-in': return 'zoom-in';
      case 'zoom-out': return 'zoom-out';
      default: return 'default';
    }
  };

  // Helper function to convert coordinates to percentage-based positioning
  const getAnnotationPosition = (annotation: Annotation, containerWidth: number, containerHeight: number) => {
    // If coordinates are already percentages (0-1), use them directly
    if (annotation.x <= 1 && annotation.y <= 1) {
      return {
        left: `${annotation.x * 100}%`,
        top: `${annotation.y * 100}%`
      };
    }
    
    // If coordinates are absolute pixels, convert to percentages
    const xPercent = (annotation.x / containerWidth) * 100;
    const yPercent = (annotation.y / containerHeight) * 100;
    
    return {
      left: `${xPercent}%`,
      top: `${yPercent}%`
    };
  };

  // PDF/image rendering logic
  let content = null;
  if (document.type === 'pdf') {
    content = (
      <div className={isMobile ? 'w-full h-auto max-w-full overflow-x-auto' : 'flex-1 h-full'} style={isMobile ? { minHeight: '60vh' } : {}}>
        {/* Document Header - moved above document rendering */}
        {!isMobile && (
          <>
            <div className="p-4 border-b bg-card">
              <h1 className="text-xl font-semibold text-foreground mb-2 truncate max-w-[60vw]" title={document.title}>{document.title}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Uploaded: {document.uploadDate ? document.uploadDate.toLocaleDateString() : 'Unknown'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>Deadline: {document.deadline ? document.deadline.toLocaleDateString() : 'No deadline set'}</span>
                </div>
                {getStatusBadge(document?.status || 'open', document?.deadline || null)}
                <a href={document.downloadUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="inline-flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </a>
              </div>
            </div>
            {/* Page navigation moved here */}
            <div className="flex items-center gap-2 p-2 border-b bg-muted/50">
              <Button variant="outline" size="sm">
                <RotateCw className="h-4 w-4" />
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {numPages}
              </span>
              <div className="flex gap-2 ml-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => {
                    setCurrentPage((p) => Math.max(1, p - 1));
                    // Scroll to the previous page
                    const prevRef = pageRefs.current[currentPage - 2];
                    if (prevRef) prevRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={currentPage >= numPages}
                  onClick={() => {
                    setCurrentPage((p) => Math.min(numPages, p + 1));
                    // Scroll to the next page
                    const nextRef = pageRefs.current[currentPage];
                    if (nextRef) nextRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
        {!isMobile ? (
          <div className="relative overflow-hidden" style={{ height: 'calc(100vh - 200px)', paddingTop: 0 }}>
          <PdfDocument
            file={document.url}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={console.error}
            loading={<div>Loading PDF...</div>}
          >
            {Array.from({ length: numPages }, (_, i) => {
              // Check if pending annotation is already saved
              const isPendingDuplicate =
                pendingAnnotation &&
                pendingAnnotation.page === i + 1 &&
                annotations.some(a =>
                  a.page === pendingAnnotation.page &&
                  Math.abs(a.x - pendingAnnotation.x) < 1e-3 &&
                  Math.abs(a.y - pendingAnnotation.y) < 1e-3
                );
              return (
                <div
                  key={i}
                  ref={el => {
                    pageRefs.current[i] = el;
                    if (!isMobile && i + 1 === currentPage) (docRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                  }}
                  className="bg-white shadow-lg border my-6 flex justify-center relative"
                  style={{
                    margin: '0 auto',
                    width: `${600 * zoom}px`,
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    cursor: getCursor(),
                  }}
                  onMouseDown={e => { console.log('onMouseDown', e); handleMouseDown(e); }}
                  onMouseMove={e => { console.log('onMouseMove', e); handleMouseMove(e); }}
                  onMouseUp={e => { console.log('onMouseUp', e); handleMouseUp(); }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onClick={(e) => handleDocumentClick(e, i + 1)}
                  onDragStart={e => { console.log('onDragStart', e); handleDragStart(e); }}
                  onWheel={(e) => {
                    if (e.ctrlKey) {
                      e.preventDefault();
                      if (e.deltaY < 0) handleZoomIn();
                      else if (e.deltaY > 0) handleZoomOut();
                    }
                  }}
                >
                  <PdfPage pageNumber={i + 1} width={600 * zoom} renderTextLayer={false} renderAnnotationLayer={false} />
                  {/* Annotations for this page */}
                  {annotations
                    .filter(annotation => annotation.page === i + 1)
                    .map(annotation => {
                      const num = annotations.findIndex(a => a.id === annotation.id) + 1;
                      const position = getAnnotationPosition(annotation, 600 * zoom, 600 * zoom);
                      return (
                        <div
                          key={annotation.id}
                          className="absolute w-5 h-5 bg-yellow-400 rounded-full border-2 border-yellow-600 cursor-pointer flex items-center justify-center text-[10px] font-semibold"
                          style={{
                            ...position,
                            transform: 'translate(-50%, -50%)',
                          }}
                          title={annotation.content}
                        >
                          {num}
                        </div>
                      );
                    })}
                  {/* Pending Annotation for this page (only if not duplicate) */}
                  {pendingAnnotation && pendingAnnotation.page === i + 1 &&
                    !annotations.some(a =>
                      a.page === pendingAnnotation.page &&
                      Math.abs(a.x - pendingAnnotation.x) < 1e-3 &&
                      Math.abs(a.y - pendingAnnotation.y) < 1e-3
                    ) && (
                      <div
                        className="absolute w-4 h-4 bg-blue-400 rounded-full border-2 border-blue-600"
                        style={{
                          left: `${pendingAnnotation.x * 100}%`,
                          top: `${pendingAnnotation.y * 100}%`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    )
                  }
                </div>
              );
            })}
          </PdfDocument>
          </div>
        ) : (
          // Mobile: just show the PDF pages, no annotation overlays
          <PdfDocument
            file={document.url}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<div className="text-center py-8">Loading PDF...</div>}
            error={<div className="text-center py-8 text-red-500">Failed to load PDF.</div>}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} className="flex justify-center my-2">
                <PdfPage pageNumber={i + 1} width={window.innerWidth - 32} renderTextLayer={false} renderAnnotationLayer={false} />
              </div>
            ))}
          </PdfDocument>
        )}
      </div>
    );
  } else if (document.type === 'image') {
    content = (
      <>
        {/* Document Header - moved above document rendering */}
        {!isMobile && (
          <div className="p-4 border-b bg-card">
            <h1 className="text-xl font-semibold text-foreground mb-2 truncate max-w-[60vw]" title={document.title}>{document.title}</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>Uploaded: {document.uploadDate ? document.uploadDate.toLocaleDateString() : 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>Deadline: {document.deadline ? document.deadline.toLocaleDateString() : 'No deadline set'}</span>
              </div>
              {getStatusBadge(document?.status || 'open', document?.deadline || null)}
              <a href={document.downloadUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="inline-flex items-center gap-1">
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              </a>
            </div>
          </div>
        )}
        <div className="relative overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div
            ref={el => {
              if (!isMobile) (docRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            }}
            style={{
              position: 'relative',
              width: 'fit-content',
              margin: '0 auto',
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              cursor: getCursor(),
            }}
            onMouseDown={e => { console.log('onMouseDown', e); handleMouseDown(e); }}
            onMouseMove={e => { console.log('onMouseMove', e); handleMouseMove(e); }}
            onMouseUp={e => { console.log('onMouseUp', e); handleMouseUp(); }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onClick={(e) => handleDocumentClick(e, 1)}
            onWheel={(e) => {
              if (e.ctrlKey) {
                e.preventDefault();
                if (e.deltaY < 0) handleZoomIn();
                else if (e.deltaY > 0) handleZoomOut();
              }
            }}
          >
            <img
              src={document.url}
              alt="Document"
              className={isMobile ? 'w-full h-auto max-w-full object-contain' : 'max-h-full max-w-full object-contain'}
              style={isMobile ? { display: 'block', margin: '0 auto' } : {}}
            />
            {/* Annotations for this image */}
            {!isMobile && annotations
              .filter(annotation => annotation.page === 1)
              .map(annotation => {
                const num = annotations.findIndex(a => a.id === annotation.id) + 1;
                // For images, use a reasonable default size since we can't easily get natural dimensions
                const containerWidth = 800;
                const containerHeight = 600;
                const position = getAnnotationPosition(annotation, containerWidth, containerHeight);
                return (
                  <div
                    key={annotation.id}
                    className="absolute w-5 h-5 bg-yellow-400 rounded-full border-2 border-yellow-600 cursor-pointer flex items-center justify-center text-[10px] font-semibold"
                    style={{
                      ...position,
                      transform: 'translate(-50%, -50%)',
                    }}
                    title={annotation.content}
                  >
                    {num}
                  </div>
                );
              })}
            {/* Pending Annotation for this image (only if not duplicate) */}
            {!isMobile && pendingAnnotation && pendingAnnotation.page === 1 &&
              !annotations.some(a =>
                a.page === pendingAnnotation.page &&
                Math.abs(a.x - pendingAnnotation.x) < 1e-3 &&
                Math.abs(a.y - pendingAnnotation.y) < 1e-3
              ) && (
                <div
                  className="absolute w-4 h-4 bg-blue-400 rounded-full border-2 border-blue-600"
                  style={{
                    left: `${pendingAnnotation.x * 100}%`,
                    top: `${pendingAnnotation.y * 100}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
          </div>
        </div>
      </>
    );
  } else {
    // Fallback for unknown document types
    content = (
      <div className="flex-1 flex justify-center items-center w-full">
        <div className="text-muted-foreground">Unsupported document type.</div>
      </div>
    );
  }
  return (
    <div
      className={
        isMobile
          ? 'w-full h-auto max-w-full overflow-x-auto flex flex-col items-center justify-center'
          : 'flex-1 h-full flex flex-col'
      }
      style={isMobile ? { minHeight: '60vh' } : {}}
    >
      {/* Only show annotation toolbar on desktop */}
      {!isMobile && (
        <VerticalToolbar
          currentTool={currentTool}
          onToolChange={setCurrentTool}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />
      )}
      {content}
      {/* Toolbar and header are now above the document, not over it */}

      {/* Document Content */}
      {/* (Removed duplicate annotation dots rendering here) */}

      {/* Annotation Input Dialog */}
      {pendingAnnotation && popupPosition && (
        <div
          className="fixed bg-card border rounded-lg shadow-lg p-1 min-w-[300px] z-50"
          style={{
            left: Math.max(8, Math.min(popupPosition.left, window.innerWidth - 320)),
            top: Math.max(8, Math.min(popupPosition.top, window.innerHeight - 200)),
          }}
        >
          <form onSubmit={handleAnnotationSubmit} className="space-y-2">
            <div className="relative rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring p-1">
              <Textarea
                value={annotationText}
                onChange={(e: any) => setAnnotationText(e.target.value)}
                placeholder="Add annotation..."
                className="min-h-[80px] resize-none rounded-lg bg-background border-0 p-3 shadow-none focus-visible:ring-0"
                autoFocus
              />
              <div className="flex items-center p-3 pt-0 justify-between">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  type="button"
                  onClick={handleAnnotationCancel}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="ml-auto gap-1.5"
                  onClick={handleAnnotationSubmit}
                >
                  Add
                  <Send className="size-3.5" />
                </Button>
              </div>
              {annotationError && <div className="text-red-500 text-xs mt-1">{annotationError}</div>}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Annotations Panel Component
const AnnotationsPanel: React.FC<{
  annotations: Annotation[];
  onRemoveAnnotation: (id: string) => void;
}> = ({ annotations, onRemoveAnnotation }) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [annotations]);
  return (
    <div className="flex flex-col h-full">
      <h3 className="font-semibold text-foreground flex items-center gap-2 mb-2">
        <MessageSquare className="h-4 w-4" />
        Annotations ({annotations.length})
      </h3>
      <ScrollArea className="flex-1 overflow-y-auto pb-2">
        <div className="space-y-2 pr-2">
          {annotations.length === 0 && (
            <div className="text-muted-foreground text-sm">No annotations yet.</div>
          )}
          {annotations.map((annotation, idx) => (
            <Card key={annotation.id} className="p-3">
              <div className="flex justify-between items-start mb-2">
                <Badge variant="outline" className="text-xs">
                  #{idx + 1} • Page {annotation.page}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    console.log('Deleting annotation with id:', annotation.id, 'review_link:', annotation.review_link || '(none)');
                    onRemoveAnnotation(annotation.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-sm text-foreground mb-2">{annotation.content}</p>
              <div className="text-xs text-muted-foreground">
                {annotation.author} • {annotation.timestamp ? new Date(annotation.timestamp).toLocaleString() : ''}
              </div>
            </Card>
          ))}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    </div>
  );
};

// Comments Panel Component
const CommentsPanel: React.FC<{
  comments: Comment[];
  onAddComment: (content: string) => void;
  onRequestChanges: () => Promise<void>;
  onApprove: () => Promise<void>;
  status: string;
  isRequesting: boolean;
  isApproving: boolean;
  requestMessage: string | null;
  showApproveDialog: boolean;
  setShowApproveDialog: (show: boolean) => void;
}> = ({ comments, onAddComment, onRequestChanges, onApprove, status, isRequesting, isApproving, requestMessage, showApproveDialog, setShowApproveDialog }) => {
  const [newComment, setNewComment] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);
  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(newComment.trim());
      setNewComment("");
    }
  };
  return (
    <div className="flex flex-col h-full">
      <h3 className="font-semibold text-foreground mb-2">Comments</h3>
      {/* Comments List */}
      <ScrollArea className="flex-1 overflow-y-auto pb-2">
        <div className="space-y-3 pr-2">
          {comments.map(comment => (
            <div key={comment.id} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                {(() => {
                  if (comment.author && comment.author.split(' ').length >= 2) {
                    const [first, last] = comment.author.split(' ');
                    return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
                  } else if (comment.author) {
                    return comment.author.split(' ').map(n => n[0]).join('').toUpperCase();
                  } else {
                    return 'R';
                  }
                })()}
              </div>
              <div className="flex-1">
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-sm text-foreground">{comment.content}</p>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {comment.author} • {comment.timestamp.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="space-y-2 sticky bottom-0 bg-card pt-2">
        <div className="relative rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring p-1">
          <Textarea
            value={newComment}
            onChange={(e: any) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="min-h-[80px] resize-none rounded-lg bg-background border-0 p-3 shadow-none focus-visible:ring-0"
          />
          <div className="flex items-center p-3 pt-0 justify-between">
            <span />
            <Button type="submit" size="sm" className="ml-auto gap-1.5">
              Send
              <Send className="size-3.5" />
            </Button>
          </div>
        </div>
        {/* Review Actions */}
        <div className="flex flex-col gap-2 pb-2">
          <Button
            variant="outline"
            className="flex-1 gap-2 h-[12rem] text-lg"
            onClick={onRequestChanges}
            disabled={isRequesting || status === 'changes-needed' || status === 'approved'}
          >
            <AlertCircle className="h-4 w-4" />
            {isRequesting ? 'Sending...' : 'Request Changes'}
          </Button>
          <Button
            variant="default"
            className="flex-1 gap-2 h-[12rem] text-lg"
            onClick={() => setShowApproveDialog(true)}
            disabled={isApproving || status === 'approved'}
          >
            <CheckCircle className="h-4 w-4" />
            Approve
          </Button>
          {requestMessage && (
            <div className="text-green-700 text-xs mt-2 text-center">{requestMessage}</div>
          )}
        </div>
        {/* Approve Confirmation Dialog */}
        {showApproveDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-xs w-full flex flex-col items-center">
              <CheckCircle className="h-8 w-8 text-green-600 mb-2" />
              <div className="font-semibold text-lg mb-2 text-center">Are you sure you want to send the final approval?</div>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={onApprove}
                  disabled={isApproving}
                >
                  {isApproving ? 'Approving...' : 'Yes'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowApproveDialog(false)}
                >
                  Keep Reviewing
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

interface ReviewPageDemoProps {
  reviewLink: string;
}

// Simple mobile detection hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

const ReviewPage: React.FC<ReviewPageDemoProps> = ({ reviewLink }) => {
  // Backend data state
  const [document, setDocument] = useState<DocumentInfo | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [reviewerName, setReviewerName] = useState<string>("");
  const [reviewerEmail, setReviewerEmail] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Add error state for comments and annotations
  const [commentError, setCommentError] = useState<string | null>(null);
  const [annotationError, setAnnotationError] = useState<string | null>(null);
  const [reviewerFirstName, setReviewerFirstName] = useState<string>("");
  const [reviewerLastName, setReviewerLastName] = useState<string>("");
  const [status, setStatus] = useState<string>('open');
  const [isRequesting, setIsRequesting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [requestMessage, setRequestMessage] = useState<string | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const reviewerLinkParam = new URLSearchParams(location.search).get('id');
  const { user, isSignedIn } = useUser();
  const [reviewerEmailFromLink, setReviewerEmailFromLink] = useState<string | null>(null);
  const [reviewerLoaded, setReviewerLoaded] = useState<boolean>(!reviewerLinkParam);

  // Helper to decide current author e-mail (owner or reviewer)
  const getAuthorEmail = () => {
    // 1. Logged-in user (creator) - for anonymous reviews, this would be the creator
    if (isSignedIn && user?.primaryEmailAddress?.emailAddress) {
      return user.primaryEmailAddress.emailAddress;
    }
    // 2. Reviewer e-mail fetched from DB (review_reviewers table)
    if (reviewerEmail) {
      return reviewerEmail;
    }
    // 3. E-mail encoded in personalised link (fallback)
    if (reviewerEmailFromLink) {
      return reviewerEmailFromLink;
    }
    // 4. For anonymous reviews, use the reviewer email from the review data
    if (isAnonymousReview) {
      // For anonymous reviews, use the reviewer email from the review data
      // This will be set when we fetch the review data
      return reviewerEmail || 'reviewer@approvia';
    }
    // 5. Final fallback
    return 'anonymous@approvia';
  };

  // Determine access permission for generic link
  // Allow access if:
  // 1. User is signed in (creator)
  // 2. User has reviewer link parameter (personalized reviewer link)
  // 3. Review is anonymous (anyone can access)
  const [isAnonymousReview, setIsAnonymousReview] = useState<boolean>(false);
  const noAccess = !isSignedIn && !reviewerLinkParam && !isAnonymousReview;

  // Fetch review info, annotations, and comments
  useEffect(() => {
    // let annotationSub: any = null;
    // let commentSub: any = null;
    async function fetchData() {
      setLoading(true);
      setError(null);
      // Fetch review request
      const { data: review, error: reviewError } = await supabase
        .from('review_requests')
        .select('*')
        .eq('review_link', reviewLink)
        .single();
      if (reviewError || !review) {
        setError('Review not found.');
        setLoading(false);
        return;
      }
      
      // Check if this is an anonymous review
      setIsAnonymousReview(review.is_anonymous === true);
      
      // For anonymous reviews, set the reviewer email from the review data
      if (review.is_anonymous === true && review.temp_reviewer_email) {
        setReviewerEmail(review.temp_reviewer_email);
      }
      
      setReviewerName(
        review.reviewer_first_name && review.reviewer_last_name
          ? `${review.reviewer_first_name} ${review.reviewer_last_name}`
          : review.reviewer_name || 'Reviewer'
      );
      setReviewerFirstName(review.reviewer_first_name || '');
      setReviewerLastName(review.reviewer_last_name || '');
      // If reviewer link present, fetch reviewer row
      if (reviewerLinkParam) {
        const { data: reviewerRow } = await supabase
          .from('review_reviewers')
          .select('*')
          .eq('reviewer_link', reviewerLinkParam)
          .single();
        if (reviewerRow && reviewerRow.reviewer_email) {
          setReviewerEmail(reviewerRow.reviewer_email);
          setReviewerEmailFromLink(reviewerRow.reviewer_email);
          setReviewerName(reviewerRow.reviewer_first_name && reviewerRow.reviewer_last_name ? `${reviewerRow.reviewer_first_name} ${reviewerRow.reviewer_last_name}` : reviewerRow.reviewer_email);
        }
        setReviewerLoaded(true);
      }
      else {
        setReviewerLoaded(true);
      }
      // Extract filename from file_url
      let filename = 'Document';
      if (review.file_url) {
        const parts = review.file_url.split('/');
        filename = parts[parts.length - 1];
      }
      setDocument({
        title: review.title || filename,
        uploadDate: review.created_at ? new Date(review.created_at) : null,
        deadline: review.deadline ? new Date(review.deadline) : null,
        downloadUrl: review.file_url,
        totalPages: review.page_count || 1,
        status: review.status || 'open',
        type: review.file_url.endsWith('.pdf') ? 'pdf' : 'image',
        url: review.file_url,
      });
      setStatus(review.status || 'open');
      // Fetch annotations
      const { data: annots, error: annotError } = await supabase
        .from('review_annotations')
        .select('*')
        .eq('review_link', reviewLink);
      if (!annotError && annots) {
        setAnnotations(annots.map((a: SupabaseAnnotation) => ({
          id: a.id.toString(),
          x: a.x,
          y: a.y,
          page: a.page,
          content: a.comment,
          timestamp: new Date(a.created_at),
          author: (a.author_email)
            ? a.author_email
            : ((review.reviewer_first_name && review.reviewer_last_name)
                ? `${review.reviewer_first_name} ${review.reviewer_last_name}`
                : review.reviewer_name || 'Reviewer'),
          avatar: (a.author_email)
            ? a.author_email.charAt(0).toUpperCase()
            : ((review.reviewer_first_name && review.reviewer_last_name)
                ? `${review.reviewer_first_name[0]}${review.reviewer_last_name[0]}`.toUpperCase()
                : (review.reviewer_name ? review.reviewer_name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'R')),
          review_link: a.review_link,
        })));
      }
      // Fetch comments
      const { data: comms, error: commError } = await supabase
        .from('review_comments')
        .select('*')
        .eq('review_link', reviewLink);
      if (!commError && comms) {
        setComments(comms.map((c: SupabaseComment) => ({
          id: c.id.toString(),
          content: c.comment,
          timestamp: new Date(c.created_at),
          author: c.author_email || c.author_name,
          avatar: c.author_email
            ? c.author_email.charAt(0).toUpperCase()
            : (c.author_name ? c.author_name.charAt(0).toUpperCase() : 'R')
        })));
      }
      setLoading(false);
      // Real-time subscriptions are disabled for now
      // annotationSub = supabase
      //   .channel('review_annotations')
      //   .on('postgres_changes', { event: '*', schema: 'public', table: 'review_annotations', filter: `review_link=eq.${reviewLink}` }, payload => {
      //     fetchData(); // re-fetch all on change
      //   })
      //   .subscribe();
      // commentSub = supabase
      //   .channel('review_comments')
      //   .on('postgres_changes', { event: '*', schema: 'public', table: 'review_comments', filter: `review_link=eq.${reviewLink}` }, payload => {
      //     fetchData();
      //   })
      //   .subscribe();
    }
    fetchData();
    // return () => {
    //   if (annotationSub) supabase.removeChannel(annotationSub);
    //   if (commentSub) supabase.removeChannel(commentSub);
    // };
    // eslint-disable-next-line
  }, [reviewLink]);

  // Add annotation
  const handleAddAnnotation = useCallback(async (newAnnotation: Omit<Annotation, 'id' | 'timestamp' | 'author'>) => {
    setAnnotationError(null);

    const insertPayload = {
      review_link: reviewLink,
      page: newAnnotation.page,
      x: newAnnotation.x,
      y: newAnnotation.y,
      comment: newAnnotation.content,
      author_email: getAuthorEmail(),
    };

    try {
      const { data, error } = await supabase
        .from('review_annotations')
        .insert(insertPayload)
        .select()
        .single();

      if (error) {
        console.error('Annotation insert error:', error);
        setAnnotationError('Failed to add annotation: ' + (error.message || String(error)));
        return;
      }

      // Use the row returned from Supabase so we have the real id / timestamp
      const annotationToAdd: Annotation = {
        id: data.id.toString(),
        x: data.x,
        y: data.y,
        page: data.page,
        content: data.comment,
        timestamp: new Date(data.created_at),
        author: getAuthorEmail(),
        avatar: getAuthorEmail().charAt(0).toUpperCase(),
        review_link: data.review_link,
      };

      setAnnotations(prev => {
        // avoid duplicates if the same id somehow exists
        if (prev.some(a => a.id === annotationToAdd.id)) return prev;
        const updated = [...prev, annotationToAdd];
        return updated;
      });
    } catch (err: any) {
      console.error('Annotation insert error (catch):', err);
      setAnnotationError('Failed to add annotation: ' + (err?.message || String(err)));
    }
  }, [reviewLink, reviewerName, reviewerFirstName, reviewerLastName, reviewerEmail]);

  // Remove annotation
  const handleRemoveAnnotation = useCallback(async (id: string) => {
    try {
      const { error } = await supabase.from('review_annotations').delete().eq('id', Number(id));
      if (error) {
        console.error('Annotation delete error:', error);
        alert('Failed to delete annotation: ' + error.message);
        return;
      }
      setAnnotations(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Annotation delete error (catch):', err);
      alert('Failed to delete annotation: ' + (err as any)?.message || String(err));
    }
  }, []);

  // Add comment
  const handleAddComment = useCallback(async (content: string) => {
    setCommentError(null);
    try {
      const { error, data } = await supabase.from('review_comments').insert({
        review_link: reviewLink,
        author_name: reviewerName,
        author_email: getAuthorEmail(),
        comment: content
      }).select();
      if (error) throw error;
      if (data && data.length > 0) {
        setComments(prev => [
          ...prev,
          {
            id: data[0].id.toString(),
            content: data[0].comment,
            timestamp: new Date(data[0].created_at),
            author: getAuthorEmail(),
            avatar: getAuthorEmail().charAt(0).toUpperCase()
          }
        ]);
      } else {
        setComments(prev => [
          ...prev,
          {
            id: Math.random().toString(),
            content,
            timestamp: new Date(),
            author: getAuthorEmail(),
            avatar: getAuthorEmail().charAt(0).toUpperCase()
          }
        ]);
      }
    } catch (err: any) {
      setCommentError('Failed to add comment: ' + (err?.message || String(err)));
    }
  }, [reviewLink, reviewerName, reviewerFirstName, reviewerLastName, reviewerEmail, reviewerEmailFromLink]);

  // Handler for Request Changes
  const handleRequestChanges = async () => {
    setIsRequesting(true);
    setRequestMessage(null);
    try {
      const { error } = await supabase
        .from('review_requests')
        .update({ status: 'changes-needed' })
        .eq('review_link', reviewLink);
      if (error) throw error;
      setStatus('changes-needed');
      setDocument((doc) => doc ? { ...doc, status: 'changes-needed' } : doc);
      setRequestMessage('Your change request has been sent');
      
      // Cancel all remaining reminders for this review since it's now completed
      const { error: reminderError } = await supabase
        .from('review_reminders')
        .update({ sent: true, sent_at: new Date().toISOString() })
        .eq('review_request_id', reviewLink)
        .eq('sent', false);
      
      if (reminderError) {
        console.error('Failed to cancel reminders:', reminderError);
        // Don't fail the entire operation for reminder errors
      } else {
        console.log('Successfully cancelled remaining reminders for review');
      }
      
      // Send changes needed email to creator
      const { data: review } = await supabase
        .from('review_requests')
        .select('*')
        .eq('review_link', reviewLink)
        .single();
      
      if (review && review.user_email) {
        const baseUrl = window.location.origin;
        const reviewUrl = `${baseUrl}/review/${review.review_link}`;
        
        try {
          await EmailService.sendChangesNeededEmail(review.user_email, {
            reviewerName: (review.reviewer_first_name && review.reviewer_last_name)
              ? `${review.reviewer_first_name} ${review.reviewer_last_name}`.trim()
              : review.reviewer_name || review.reviewer_email || 'Reviewer',
            reviewUrl,
            comments: comments.length > 0 ? comments.map(c => c.content).join('\n\n') : undefined,
            annotations: annotations.length > 0 ? `${annotations.length} annotation(s) added` : undefined,
          });
        } catch (emailError) {
          console.error('Failed to send changes needed email:', emailError);
        }
      }
    } catch (err) {
      setRequestMessage('Failed to send change request');
    } finally {
      setIsRequesting(false);
    }
  };

  // Handler for Approve
  const handleApprove = async () => {
    setIsApproving(true);
    setRequestMessage(null);
    try {
      const { error } = await supabase
        .from('review_requests')
        .update({ status: 'approved' })
        .eq('review_link', reviewLink);
      if (error) throw error;
      setStatus('approved');
      setDocument((doc) => doc ? { ...doc, status: 'approved' } : doc);
      setShowApproveDialog(false);
      
      // Cancel all remaining reminders for this review since it's now completed
      const { error: reminderError } = await supabase
        .from('review_reminders')
        .update({ sent: true, sent_at: new Date().toISOString() })
        .eq('review_request_id', reviewLink)
        .eq('sent', false);
      
      if (reminderError) {
        console.error('Failed to cancel reminders:', reminderError);
        // Don't fail the entire operation for reminder errors
      } else {
        console.log('Successfully cancelled remaining reminders for review');
      }
      
      // Send approval email to creator
      const { data: review } = await supabase
        .from('review_requests')
        .select('*')
        .eq('review_link', reviewLink)
        .single();
      
      if (review && review.user_email) {
        const baseUrl = window.location.origin;
        const reviewUrl = `${baseUrl}/review/${review.review_link}`;
        
        try {
          await EmailService.sendApprovalEmail(review.user_email, {
            reviewerName: (review.reviewer_first_name && review.reviewer_last_name)
              ? `${review.reviewer_first_name} ${review.reviewer_last_name}`.trim()
              : review.reviewer_name || review.reviewer_email || 'Reviewer',
            reviewUrl,
            approvalDate: new Date().toLocaleDateString(),
            comments: comments.length > 0 ? comments.map(c => c.content).join('\n\n') : undefined,
          });
        } catch (emailError) {
          console.error('Failed to send approval email:', emailError);
        }
      }
    } catch (err) {
      setRequestMessage('Failed to approve review');
    } finally {
      setIsApproving(false);
    }
  };

  if (loading || !reviewerLoaded) {
    return <div className="h-screen flex items-center justify-center"><div>Loading...</div></div>;
  }
  if (error || !document) {
    return <div className="h-screen flex items-center justify-center"><div>{error || 'Not found'}</div></div>;
  }

  if (noAccess) {
    return (
      <div className="h-screen flex items-center justify-center p-4 text-center">
        <div className="max-w-md space-y-4">
          <h1 className="text-xl font-semibold">Authentication required</h1>
          <p className="text-muted-foreground">You must be signed in or use your personalised reviewer link to access this review.</p>
          <a
            href={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="inline-block"
          >
            <Button variant="default" className="mt-4">Login</Button>
          </a>
        </div>
      </div>
    );
  }

  // Debug: log annotations state before rendering children
  console.log('ReviewPage annotations state:', annotations);

  if (isMobile) {
    // Map comments to ensure timestamp is a string
    const mobileComments = comments.map((c) => ({
      ...c,
      timestamp: typeof c.timestamp === 'string' ? c.timestamp : c.timestamp.toLocaleString(),
    }));
    // Determine the correct status for mobile (same logic as desktop)
    const getMobileStatus = () => {
      if (status === 'approved') return 'approved';
      if (status === 'changes-needed') return 'overdue'; // changes-needed shows as overdue in mobile
      if (status === 'open' && document?.deadline && new Date(document.deadline) < new Date()) {
        return 'overdue';
      }
      return 'open';
    };

    return (
      <MobileReviewPage
        documentTitle={document?.title || ''}
        status={getMobileStatus()}
        documentUrl={document?.url || ''}
        documentType={document?.type === 'pdf' ? 'pdf' : 'image'}
        uploadDate={document?.uploadDate ? document.uploadDate.toLocaleDateString() : ''}
        deadline={document?.deadline ? document.deadline.toLocaleDateString() : ''}
        reviewerName={reviewerName}
        comments={mobileComments}
        isReviewer={true}
        onApprove={handleApprove}
        onRequestChanges={handleRequestChanges}
        isRequesting={isRequesting}
        requestMessage={requestMessage}
        onDownload={() => window.open(document?.url || '', '_blank')}
        onAddComment={handleAddComment}
      />
    );
  }

  return (
    <div className={`h-screen flex bg-background ${isMobile ? 'flex-col' : ''}`}>
      {/* Fixed Main Header with Long Logo and Button Overlapping Right Panel */}
      <div className={`fixed top-0 left-0 w-full flex items-center justify-between pl-8 pr-4 bg-white z-30 shadow`} style={{ height: '56px' }}>
        <a href="https://approvia.io" target="_blank" rel="noopener noreferrer">
          <img
            src={longLogo}
            alt="Approvia Long Logo"
            className="block"
            style={{ maxWidth: '450px', height: '40px', objectFit: 'contain' }}
          />
        </a>
        <a href="https://approvia.io" target="_blank" rel="noopener noreferrer">
          <Button variant="default" size="sm" className="flex items-center gap-2">
            <Star className="h-4 w-4 mr-1" />
            Create Your Review
          </Button>
        </a>
      </div>
      {/* Main Document Panel */}
      <div className={`${isMobile ? 'w-full' : 'flex-1 w-4/5'} overflow-y-scroll`} style={{ paddingTop: '56px' }}>
        <DocumentViewer
          document={document}
          annotations={isMobile ? [] : annotations}
          onAddAnnotation={isMobile || !reviewerLoaded ? undefined : handleAddAnnotation}
          annotationError={annotationError}
          setAnnotationError={setAnnotationError}
          isMobile={isMobile}
        />
        {annotationError && <div className="text-red-500 text-xs p-2">{annotationError}</div>}
      </div>
      {/* Sidebar: Annotations + Comments */}
      <div className={`${isMobile ? 'w-full border-t' : 'w-1/5 min-w-[300px] border-l'} bg-card flex flex-col`} style={{ paddingTop: '56px' }}>
        {!isMobile && (
          <>
            <div className="p-4 border-b text-sm text-muted-foreground space-y-2">
              <h3 className="font-semibold text-foreground">How to review</h3>
              <ul className="list-disc pl-4 space-y-1">
                <li>Use the toolbar on the left to pan, annotate or zoom the document.</li>
                <li>Add annotations directly on the page or leave general comments below.</li>
                <li>When you’re done, press “Request Changes” to send feedback or “Approve” if everything looks good.</li>
              </ul>
            </div>
            <div className="flex-1 p-4 border-b overflow-hidden">
              <AnnotationsPanel
                annotations={annotations}
                onRemoveAnnotation={handleRemoveAnnotation}
              />
            </div>
          </>
        )}
        <div className="flex-1 p-4 overflow-hidden flex flex-col">
          {reviewerLoaded ? (
          <CommentsPanel
            comments={comments}
            onAddComment={handleAddComment}
            onRequestChanges={handleRequestChanges}
            onApprove={handleApprove}
            status={status}
            isRequesting={isRequesting}
            isApproving={isApproving}
            requestMessage={requestMessage}
            showApproveDialog={showApproveDialog}
            setShowApproveDialog={setShowApproveDialog}
          />
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading reviewer…</div>
          )}
          {commentError && <div className="text-red-500 text-xs p-2">{commentError}</div>}
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
